import { KeyValue, TitleCasePipe } from '@angular/common';
import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';

import {
  FloaterBasicCoverProductInfo,
  IContactAddress,
  IContactEmail,
  IContactNumber,
  IName,
  ProposalInformation,
} from '@shared/interfaces';
import {
  extractDefaultValue,
  getArrangedName,
  thousandSeparatorAdd,
} from '@shared/utils';

import { DateTimeFormatService } from '../../../../services/date-time-format.service';
import { ClientSetupResponseDTO } from '../../../setup/general/services/client-data.service';

@Component({
  selector: 'app-print-medical-proposal-component',
  templateUrl: './print-medical-proposal.component.html',
  styleUrls: ['./print-medical-proposal.component.scss'],
  standalone: false,
})
export class MedicalProposalTemplateComponent implements OnInit, OnChanges {
  @Input() proposal_information: ProposalInformation;
  @Input() ref_number: string;
  @Input() client_information: ClientSetupResponseDTO;
  @Input() created_on: string;
  @Input() communication_modes: string;
  @Input() communication_languages: string;
  @Input() isMedicalGroup: boolean;
  @Input() coverages: KeyValue<string, string>[];
  @Input() covers: KeyValue<string, string>[];

  thousandSeparatorAdd = thousandSeparatorAdd;

  public coverSectionNumber = '03.';
  public creditSectionNumber = '05.';

  floaterData: FloaterBasicCoverProductInfo;

  constructor(
    public dateTimeFormatService: DateTimeFormatService,
    public titleCasePipe: TitleCasePipe
  ) {}

  ngOnInit(): void {
    this.onInit();
  }

  ngOnChanges(_changes: SimpleChanges): void {
    this.onInit();
  }

  onInit() {
    const memberCount =
      this.proposal_information?.member_details?.length || 0;
    const coverCount =
      this.proposal_information?.additional_covers_details?.covers?.length || 0;

    if (!this.isMedicalGroup && memberCount === 0) {
      this.coverSectionNumber = '03.';
    }
    if (!this.isMedicalGroup && memberCount > 0) {
      this.coverSectionNumber = '04.';
    }

    if (this.isMedicalGroup) {
      this.creditSectionNumber = '03.';
      this.floaterData =
        this.proposal_information?.medical_group_scheme_products?.floaterBasisCovers;
    }

    if (!this.isMedicalGroup && coverCount === 0 && memberCount === 0) {
      this.creditSectionNumber = '03.';
    }

    if (!this.isMedicalGroup && coverCount > 0 && memberCount === 0) {
      this.creditSectionNumber = '04.';
    }

    if (!this.isMedicalGroup && coverCount === 0 && memberCount > 0) {
      this.creditSectionNumber = '04.';
    }

    if (!this.isMedicalGroup && coverCount > 0 && memberCount > 0) {
      this.creditSectionNumber = '05.';
    }
  }

  getFormattedDate(value: string | Date) {
    if (!value) return '-';
    return this.dateTimeFormatService?.formatDateAsString(new Date(value));
  }

  getName(name: IName) {
    return getArrangedName(name);
  }

  getPhone(contact: IContactNumber[]) {
    return extractDefaultValue({
      type: 'PHONE',
      value: contact,
    });
  }

  getEmail(email: IContactEmail[]) {
    return extractDefaultValue({
      type: 'EMAIL',
      value: email,
    });
  }

  getAddress(address: IContactAddress[]) {
    return extractDefaultValue({
      type: 'ADDRESS',
      value: address,
    });
  }

  getStringForBoolean(value: boolean): 'YES' | 'NO' {
    return value ? 'YES' : 'NO';
  }

  getCoverageName(key: string): string {
    return this.coverages?.find((a) => a?.key === key)?.value;
  }

  getCoverName(key: string): string {
    return this.covers?.find((a) => a?.key === key)?.value;
  }

  get groupInfo(): any {
    return (this.proposal_information as any)?.life_health_group_proposal_information;
  }

  get individualInfo(): any {
    return (this.proposal_information as any)?.life_health_proposal_information;
  }

  get lhSummary(): any {
    return (this.proposal_information as any)?.life_health_print_summary;
  }

  getMemberName(m: any): string {
    return (
      [m?.salutation, m?.first_name, m?.surname]
        .filter((v) => !!v && `${v}`.trim())
        .join(' ') || '-'
    );
  }

  getDependentLabel(key: string): string {
    const labels: Record<string, string> = {
      member: 'Principal Member',
      spouse_adult: 'Spouse / Adult Dependents',
      child: 'Child Dependents',
      special_dependent: 'Special Dependents (0-59 years)',
      parent: 'Parents',
    };
    return (
      labels[key] ||
      `${key || 'Dependent'}`.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
    );
  }

  formatAmount(val: any): string {
    if (val === null || val === undefined || val === '') return '-';
    const num = Number(val);
    if (isNaN(num)) return val.toString();
    return thousandSeparatorAdd(num);
  }

  getDependentsText(dependents: any[]): string {
    if (!Array.isArray(dependents) || dependents.length === 0) return '-';
    return dependents
      .map((d) => `${d.relationship || 'Dependent'}: ${d.count || 0}`)
      .join(', ');
  }
}
